import _ from 'lodash';
import {app, idba, onair} from "../modules/config.ts";
import {Utils} from "../modules/utils.ts";
import {IDBTables} from "../modules/idb.service.ts";
import type {ApiParametersData, IDBData} from "../modules/interfaces.ts";
import type {DaoInit} from "./dao-init.ts";
import type {DaoClient} from "./dao-client.ts";
import type {DaoCategory} from "./dao-category.ts";
import type {DaoNotice} from "./dao-notice.ts";
import type {DaoVoice} from "./dao-voice.ts";
import type {BrandBase} from "../brands/brand.base.ts";


export class DaoApp {
  [key: string]: unknown;

  public isDebug: boolean = false;

  public cfg: BrandBase;

  public online: boolean = true;

  public app_version?: string;
  public app_version_code?: string;

  public site: string;
  public package: string;
  public title: string;
  public name: string;
  public hash: string;

  public version: number;
  public version_code: string;
  public device: string;

  public imagePath: string;
  public cssPath: string;
  public jsPath: string;

  public url: string;
  public cscenter: string;
  public csphone: string;

  public config_no: string | null = null;

  public uuid: string = Utils.uuid();
  public os: string = Utils.osname();
  public regist: string = Utils.toDateString();
  public ip: string = '';
  public language: string = 'ko';
  public timezone: string = 'Asia/Seoul';
  public connect: string = '';
  public connect_date: string = '';
  public cryptkey: string | null  = null;
  public auth: string  = '';
  public ws_endpoint: string  = '';
  public transfer: string  = '';

  public backup: number = _.random(0, 59);

  public inited: DaoInit | null = null;
  public user: DaoClient | null = null;

  public notices: DaoNotice[] = [];
  public categories: DaoCategory[] = [];
  public voices: DaoVoice[] = [];

  public image: string = '';
  public image_default: string = '';

  private api: string = '';
  public apis: string[];

  private stream: string = '';
  public streams: string[];

  constructor(cfg: BrandBase) {
    this.cfg = cfg;
    this.site = cfg.site;
    this.package = cfg.package;
    this.title = `${cfg.name} 웹플레이어`;
    this.name = cfg.name;
    this.hash = cfg.hash;

    this.device = cfg.device;
    this.version = cfg.version;
    this.version_code = cfg.version_code;

    this.url = cfg.url;
    this.cscenter = cfg.cscenter;
    this.csphone = cfg.csphone;

    this.imagePath = cfg.imagePath;
    this.cssPath = cfg.cssPath;
    this.jsPath = cfg.jsPath;

    this.image = cfg.image;
    this.image_default = cfg.image_default;
    this.apis = cfg.apis;
    this.streams = cfg.streams;
  }

  public get iOS() {
    return ['iOS', 'macOS']
  }

  public get apiUrl(): string {
    if(_.isEmpty(this.api)) {
      const index: number = _.random(0, this.apis.length);
      this.apiUrl = this.apis[index] ??  this.apis[0];
    }
    return this.api;
  }

  public set apiUrl(url: string) {
    this.api = url;
  }

  public get streamUrl(): string {
    if(_.isEmpty(this.stream)) {
      const index: number = _.random(0, this.streams.length);
      this.streamUrl = this.streams[index] ??  this.streams[0];
    }
    return this.stream;
  }

  public set streamUrl(url: string) {
    this.stream = url;
  }

  public getCover(url: string | undefined): string {
    return url ? url : app.image_default;
  }

  public async init(inited: DaoInit) {
    document.title = this.title;

    /// fav icon
    let link: HTMLLinkElement | null = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = `${this.imagePath}/favicon.png`;

    //style sheet
    let stylesheet: HTMLLinkElement | null = document.getElementById("brandstyle") as HTMLLinkElement;
    if (!stylesheet) {
      stylesheet = document.createElement("link");
      stylesheet.rel = "stylesheet";
      document.head.appendChild(stylesheet);
    }
    stylesheet.href = `${this.cssPath}/style.css`;

    this.inited = inited;

    // 디비 저장 데이터
    let data = await idba.getByField<IDBData>(IDBTables.configs, 'key', 'init');
    if(data) {
      const json = Utils.fromJson<{ [key: string]: never }>((data.value as string ?? Utils.toJson({}, true)), undefined, true);
      if(json) {
        this.uuid = json.uuid;
        this.os = this.os ?? json.os;
        this.regist = json.regist;
        this.connect = json.connect;
        this.connect_date = json.connect_date;

        if(!_.isEmpty(json.auth)) {
          this.user =  Object.assign(json.auth, {saved: true}) as DaoClient;
        }
      }
    }
    data = await this.save(data);

    this.config_no = data._id;
  }

  /**
   * 디비 저장
   * @param data
   */
  public async save(data: IDBData | undefined): Promise<IDBData> {
    if(data != undefined) {
      data.value = Utils.toJson(this.toDatabase(), true);
      await idba.save<IDBData>(IDBTables.configs, data);
    } else {
      data = { key: 'init', value: Utils.toJson(this.toDatabase(), true) } as IDBData;
      data._id = await idba.add<IDBData>(IDBTables.configs, data) as string;
    }
    return data;
  }

  /**
   * IDB에 저장될 정보들
   * 인증정보도 포함됨
   */
  public toDatabase() {
    return {
      uuid: this.uuid,
      ip: this.ip,
      os: this.os,
      version: this.version,
      version_code: this.version_code,
      device: this.device,
      language: this.language,
      timezone: this.timezone,
      regist: this.regist,
      auth: ((this.user && (this.user.saved || this.user.passthru)) ? { id: this.user.id, password: this.user.password, passthru: this.user.passthru } : null),
      cryptkey: this.cryptkey,
      connect: this.connect,
      connect_date: this.connect_date,
    };
  }

  public getParameters(): ApiParametersData {
    const datas = {
      site: this.site,
      uuid: this.uuid,
      device: this.device,
      version: this.version_code,
      version_label: this.version,
      ip: this.ip,
      os: this.os,
      registed: this.regist,
      date: Utils.toDateString(onair.getDate()),
      connection: Number(this.connect)
    } as unknown as ApiParametersData;

    if(this.user != null) {
      datas.authkey = this.cryptkey;
      datas.auth = this.auth;
      datas.id = this.user.id!;
      datas.group_id = this.user.group_id!;
    }

    return datas;
  }

  public isLogin(): boolean {
    return !_.isEmpty(this.user?.group_id) && Number(this.user?.period_remain) > 0;
  }

  public getClientName(type: boolean = true): string | null {
    return this.user ? `${this.user.name}${type ? `(${this.user.type_name})` : ''}` : null;
  }

  public getClientPeriod(): string | null {
    // ~2030-12-31까지
    return this.user && this.user.period_end ? `~${this.user.period_end.substring(0, 10)}까지` : '';
  }
}